import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import {
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const WorkPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pending: 0,
    accepted: 0,
    completed: 0
  });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [workId, setWorkId] = useState(null);
  const [lastJobId, setLastJobId] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.get('/applications');
      const applications = response.data;
      // Filter to current month based on applied_at timestamp
      const now = new Date();
      const thisYear = now.getFullYear();
      const thisMonth = now.getMonth();
      const inThisMonth = (app) => {
        if (!app || !app.applied_at) return false;
        const d = new Date(app.applied_at);
        return d.getFullYear() === thisYear && d.getMonth() === thisMonth;
      };

  const getPosition = () => new Promise((resolve, reject) => {
    try {
      if (!navigator.geolocation) return resolve({ coords: { latitude: null, longitude: null } });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        () => resolve({ coords: { latitude: null, longitude: null } }),
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } catch (_) {
      resolve({ coords: { latitude: null, longitude: null } });
    }
  });

  const handleCheckIn = async (jobId) => {
    try {
      if (!user) return;
      setWorking(true);
      const token = localStorage.getItem('token');
      const resPos = await getPosition();
      const body = {
        job_id: jobId,
        latitude: resPos?.coords?.latitude,
        longitude: resPos?.coords?.longitude,
        device_time: new Date().toISOString(),
      };
      const resp = await fetch(`${API}/attendance/clock-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        // If QR is required on the backend, we may get 403 here. Prompt for qr_token and retry once.
        if (resp.status === 403) {
          const qr = window.prompt('現地のQRコードを読み取り、トークンを入力してください');
          if (qr && qr.trim()) {
            const retryBody = { ...body, qr_token: qr.trim() };
            const resp2 = await fetch(`${API}/attendance/clock-in`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(retryBody)
            });
            if (!resp2.ok) throw new Error(`check-in failed: ${resp2.status}`);
            const data2 = await resp2.json();
            setWorkId(data2.id);
            setLastJobId(jobId);
            setStats((s) => ({ ...s }));
            alert('チェックインしました');
            return;
          }
        }
        throw new Error(`check-in failed: ${resp.status}`);
      }
      const data = await resp.json();
      setWorkId(data.id);
      setLastJobId(jobId);
      setStats((s) => ({ ...s }));
      alert('チェックインしました');
    } catch (e) {
      console.error(e);
      alert('チェックインに失敗しました');
    } finally {
      setWorking(false);
    }
  };

  const handleCheckOut = async () => {
    try {
      if (!user || !workId) return;
      setWorking(true);
      const token = localStorage.getItem('token');
      const resPos = await getPosition();
      const body = {
        job_id: lastJobId,
        latitude: resPos?.coords?.latitude,
        longitude: resPos?.coords?.longitude,
        device_time: new Date().toISOString(),
      };
      const resp = await fetch(`${API}/attendance/clock-out`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error(`check-out failed: ${resp.status}`);
      await resp.json();
      alert('チェックアウトしました（勤務完了）');
      setWorkId(null);
      setLastJobId('');
    } catch (e) {
      console.error(e);
      alert('チェックアウトに失敗しました');
    } finally {
      setWorking(false);
    }
  };

      const thisMonthApps = Array.isArray(applications)
        ? applications.filter(inThisMonth)
        : [];

      const newStats = {
        pending: thisMonthApps.filter(app => app.status === 'pending').length,
        accepted: thisMonthApps.filter(app => app.status === 'accepted').length,
        completed: thisMonthApps.filter(app => app.status === 'completed').length
      };
      
      setStats(newStats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // エラーが発生してもページは表示する
    } finally {
      setLoading(false);
    }
  };

  // Scoped helpers (outside fetchStats) to avoid runtime reference errors
  const getPositionSafe = () => new Promise((resolve) => {
    try {
      if (!navigator.geolocation) return resolve({ coords: { latitude: null, longitude: null } });
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        () => resolve({ coords: { latitude: null, longitude: null } }),
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } catch (_) {
      resolve({ coords: { latitude: null, longitude: null } });
    }
  });

  const doCheckIn = async (jobId) => {
    try {
      if (!user) return;
      setWorking(true);
      const token = localStorage.getItem('token');
      const resPos = await getPositionSafe();
      const body = {
        job_id: jobId,
        latitude: resPos?.coords?.latitude,
        longitude: resPos?.coords?.longitude,
        device_time: new Date().toISOString(),
      };
      const resp = await fetch(`${API}/attendance/clock-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) {
        if (resp.status === 403) {
          const qr = window.prompt('現地のQRコードを読み取り、トークンを入力してください');
          if (qr && qr.trim()) {
            const retryBody = { ...body, qr_token: qr.trim() };
            const resp2 = await fetch(`${API}/attendance/clock-in`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(retryBody)
            });
            if (!resp2.ok) throw new Error(`check-in failed: ${resp2.status}`);
            const data2 = await resp2.json();
            setWorkId(data2.id);
            setLastJobId(jobId);
            setStats((s) => ({ ...s }));
            alert('チェックインしました');
            return;
          }
        }
        throw new Error(`check-in failed: ${resp.status}`);
      }
      const data = await resp.json();
      setWorkId(data.id);
      setLastJobId(jobId);
      setStats((s) => ({ ...s }));
      alert('チェックインしました');
    } catch (e) {
      console.error(e);
      alert('チェックインに失敗しました');
    } finally {
      setWorking(false);
    }
  };

  const doCheckOut = async () => {
    try {
      if (!user || !workId) return;
      setWorking(true);
      const token = localStorage.getItem('token');
      const resPos = await getPositionSafe();
      const body = {
        job_id: lastJobId,
        latitude: resPos?.coords?.latitude,
        longitude: resPos?.coords?.longitude,
        device_time: new Date().toISOString(),
      };
      const resp = await fetch(`${API}/attendance/clock-out`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error(`check-out failed: ${resp.status}`);
      await resp.json();
      alert('チェックアウトしました（勤務完了）');
      setWorkId(null);
      setLastJobId('');
    } catch (e) {
      console.error(e);
      alert('チェックアウトに失敗しました');
    } finally {
      setWorking(false);
    }
  };

  const menuItems = [
    {
      id: 'upcoming',
      title: '今後の予定',
      description: 'マッチング成立した\nお仕事が表示されます',
      icon: CalendarDaysIcon,
      path: '/work/upcoming',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      textColor: 'text-blue-900'
    },
    {
      id: 'search',
      title: '仕事をさがす',
      description: '新しいお仕事を\n見つけましょう',
      icon: MagnifyingGlassIcon,
      path: '/jobs',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      textColor: 'text-green-900'
    },
    {
      id: 'biters',
      title: 'バイターズ',
      description: '特別なお仕事情報を\nチェックしよう',
      icon: null, // カスタムボタンとして処理
      path: '/work/biters',
      bgColor: 'bg-yellow-400',
      iconColor: 'text-yellow-900',
      textColor: 'text-yellow-900',
      isSpecial: true
    },
    {
      id: 'history',
      title: 'これまでの仕事',
      description: '過去の勤務履歴を\n確認できます',
      icon: ClockIcon,
      path: '/work/history',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      textColor: 'text-purple-900'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-6 w-6 mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold text-gray-900">はたらく</h1>
            <div className="w-8"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-6">
        {/* Welcome Message */}
        <div className="mb-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            こんにちは、{user?.name}さん
          </h2>
          <p className="text-gray-600">
            今日も頑張りましょう！
          </p>
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-2 gap-4">
          {menuItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`
                ${item.bgColor} 
                ${item.textColor}
                rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 
                transform hover:scale-105 active:scale-95
                relative overflow-hidden
              `}
            >
              {/* Special styling for Biters button */}
              {item.isSpecial ? (
                <div className="flex flex-col items-center text-center h-32">
                  <div className="w-12 h-12 mb-3 flex items-center justify-center">
                    {/* Custom icon or text for Biters */}
                    <div className="text-2xl font-bold"></div>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-80 whitespace-pre-line leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center h-32">
                  <div className={`w-12 h-12 ${item.iconColor} mb-3 flex items-center justify-center`}>
                    <item.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-80 whitespace-pre-line leading-relaxed">
                    {item.description}
                  </p>
                </div>
              )}

              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-white bg-opacity-0 hover:bg-opacity-10 transition-all duration-200 rounded-xl"></div>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 bg-white rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-4">今月の活動</h3>
          {loading ? (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-300">...</div>
                <div className="text-xs text-gray-600">応募中</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-300">...</div>
                <div className="text-xs text-gray-600">マッチング</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-300">...</div>
                <div className="text-xs text-gray-600">完了</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{stats.pending}</div>
                <div className="text-xs text-gray-600">応募中</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
                <div className="text-xs text-gray-600">マッチング</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">{stats.completed}</div>
                <div className="text-xs text-gray-600">完了</div>
              </div>
            </div>
          )}
        </div>

        {/* Tips */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
              <span className="text-blue-600 text-sm"></span>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-1">ヒント</h4>
              <p className="text-sm text-blue-700">
                プロフィールを充実させると、マッチング率がアップします！
              </p>
              <div className="mt-4 flex gap-2">
                <button
                  disabled={working}
                  onClick={() => {
                    const jobId = prompt('チェックインする求人IDを入力してください');
                    if (jobId) doCheckIn(jobId);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${working ? 'bg-gray-300' : 'bg-green-600 hover:bg-green-700'} text-white`}
                >
                  出勤（チェックイン）
                </button>
                <button
                  disabled={working || !lastJobId}
                  onClick={doCheckOut}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${working || !workId ? 'bg-gray-300' : 'bg-purple-600 hover:bg-purple-700'} text-white`}
                >
                  退勤（チェックアウト）
                </button>
              </div>
              {workId && (
                <p className="text-xs text-blue-700 mt-2">現在の勤務ID: {workId}（求人ID: {lastJobId}）</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkPage;